/**
 * 销售管理 · 团队名册抽屉（运营主管 advisor:team_view / advisor:transfer，设计 D6 / D7 / I4 / I5）。
 *
 * 点销售行 → 右侧抽屉列出这位销售名下的客户：**只有称呼、阶段、订单数、创建与最近活跃**，
 * 没有手机号、背景、讲述内容（服务端按白名单投影，这里的类型也不含）。
 * 持有 advisor:transfer 的人可以多选（最多 20 位）→ 选接收人 → 填必填理由（5–200 字，实时校验）→ 确认转交；
 * 服务端另有 24 小时累计 ≤ 50 位的额度，超了会把「已转多少、还能转多少」原样提示出来。
 * 只持 advisor:team_view 的人只读：没有勾选框与转交表单。
 *
 * 超管自己的转交入口（客户列表勾选转交 / 销售管理「转交全部」）不在这里，也不受这些约束。
 */
import { useState } from 'react';
import { Alert, Button, Drawer, Form, Input, Select, Space, Table, Tag, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usePermission } from '../../../hooks/usePermission';
import { PERMISSIONS } from '../../../contracts/permissions';
import { useSession } from '../../../stores/session';
import {
  listAdvisorSales,
  listAdvisorTeamCustomers,
  transferAdvisorCustomers,
  type SalesOverviewRowDto,
  type TeamCustomerDto,
} from '../../../api/advisor';
import { STAGE_META, STAGE_OPTIONS, lastNarratedText } from '../customers/stage';

const { Text } = Typography;

/** 与服务端 constants/advisor-team.ts 的 TEAM_TRANSFER_LIMITS 同值：这里只做展示与实时校验，真正的拦截在服务端 */
export const TEAM_TRANSFER_MAX_CUSTOMERS = 20;
export const TEAM_TRANSFER_DAILY_LIMIT = 50;
export const TRANSFER_REASON_MIN = 5;
export const TRANSFER_REASON_MAX = 200;

const PAGE_SIZE = 20;

const errText = (e: any, fallback: string) => e?.response?.data?.message ?? fallback;

/** 理由字数：去首尾空白，按字符（码点）数，与服务端口径一致 */
export function reasonLength(value?: string): number {
  return [...(value ?? '').trim()].length;
}

/** 理由校验：合格返回 null，否则返回给人看的一句话 */
export function reasonError(value?: string): string | null {
  const n = reasonLength(value);
  if (n === 0) return '请填写转交理由';
  if (n < TRANSFER_REASON_MIN) return `理由至少 ${TRANSFER_REASON_MIN} 个字（现在 ${n} 个）`;
  if (n > TRANSFER_REASON_MAX) return `理由最多 ${TRANSFER_REASON_MAX} 个字（现在 ${n} 个）`;
  return null;
}

function formatTs(ms: number | null): string {
  if (ms == null) return '-';
  return new Date(ms).toLocaleString('zh-CN', { hour12: false });
}

interface RosterBodyProps {
  sales: SalesOverviewRowDto;
}

function RosterBody({ sales }: RosterBodyProps) {
  const queryClient = useQueryClient();
  const canTransfer = usePermission(PERMISSIONS.ADVISOR_TRANSFER);
  const canManage = usePermission(PERMISSIONS.ADVISOR_MANAGE);
  const actorId = useSession((s) => s.actorId);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<string[]>([]);
  const [form] = Form.useForm<{ toAdminId: string; reason: string }>();
  const reason = Form.useWatch('reason', form);

  const roster = useQuery({
    queryKey: ['advisor-team-roster', sales.id, page],
    queryFn: () => listAdvisorTeamCustomers({ salesId: sales.id, page, pageSize: PAGE_SIZE }),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const salesList = useQuery({ queryKey: ['advisor-sales'], queryFn: listAdvisorSales, enabled: canTransfer });

  const transfer = useMutation({
    mutationFn: transferAdvisorCustomers,
    onSuccess: (data) => {
      const skipped = data.skipped.length ? `，${data.skipped.length} 位未转（已属于对方或已被转走）` : '';
      message.success(`已转交 ${data.transferred.length} 位客户${skipped}`);
      setSelected([]);
      setPage(1);
      form.resetFields();
      // 归属变了：团队概览、名册、首页工作台、客户列表都要重取
      for (const key of ['advisor-sales-overview', 'advisor-team-roster', 'advisor-workbench', 'advisor-customers']) {
        queryClient.invalidateQueries({ queryKey: [key] });
      }
    },
    onError: (e) => {
      message.error(errText(e, '转交失败'));
    },
  });

  /** 跨页保留勾选，但总数夹在上限内：全选一整页也不会越过 20 位 */
  function onSelectionChange(keys: React.Key[]) {
    const incoming = keys as string[];
    const kept = selected.filter((k) => incoming.includes(k));
    const added = incoming.filter((k) => !selected.includes(k));
    const next = [...kept, ...added].slice(0, TEAM_TRANSFER_MAX_CUSTOMERS);
    if (kept.length + added.length > TEAM_TRANSFER_MAX_CUSTOMERS) {
      message.warning(`一次最多转交 ${TEAM_TRANSFER_MAX_CUSTOMERS} 位客户`);
    }
    setSelected(next);
  }

  const columns: ColumnsType<TeamCustomerDto> = [
    {
      title: '称呼',
      dataIndex: 'displayName',
      render: (v: string) => <Text strong>{v}</Text>,
    },
    {
      title: '阶段（书）',
      render: (_, row) => {
        const tags = STAGE_OPTIONS.filter((s) => (row.stageCounts[s] ?? 0) > 0);
        return tags.length === 0 ? (
          <Text type="secondary">还没有订单</Text>
        ) : (
          <Space size={2} wrap>
            {tags.map((s) => (
              <Tag key={s} color={STAGE_META[s].color} style={{ marginInlineEnd: 0 }}>
                {STAGE_META[s].label} {row.stageCounts[s]}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: '订单',
      width: 130,
      render: (_, row) => (
        <Text>
          已付 {row.paidCount} · 待付 {row.awaitingCount}
        </Text>
      ),
    },
    { title: '创建', dataIndex: 'createdAt', width: 170, render: (v: number) => formatTs(v) },
    {
      title: '最近活跃',
      width: 100,
      render: (_, row) =>
        Object.keys(row.stageCounts).length === 0 ? <Text type="secondary">-</Text> : lastNarratedText(row.lastActiveAt),
    },
  ];

  const selectionFull = selected.length >= TEAM_TRANSFER_MAX_CUSTOMERS;

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        message="团队名册只显示称呼、阶段、订单数等，不含联系方式与讲述内容。"
        description={
          canTransfer
            ? `勾选客户后可转交给其他销售（不能转给自己）：一次最多 ${TEAM_TRANSFER_MAX_CUSTOMERS} 位，你 24 小时内累计最多转交 ${TEAM_TRANSFER_DAILY_LIMIT} 位；必须写明理由，每次转交都会留痕并让超管看到。`
            : '你只有查看权限，不能转交。'
        }
      />

      <Table<TeamCustomerDto>
        rowKey="customerId"
        size="small"
        loading={roster.isLoading}
        dataSource={roster.data?.items ?? []}
        columns={columns}
        rowSelection={
          canTransfer
            ? {
                selectedRowKeys: selected,
                preserveSelectedRowKeys: true,
                onChange: onSelectionChange,
                // 已选满 20 位后，没选中的行不能再勾
                getCheckboxProps: (row) => ({ disabled: selectionFull && !selected.includes(row.customerId) }),
              }
            : undefined
        }
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: roster.data?.total ?? 0,
          showSizeChanger: false,
          showTotal: (t) => `共 ${t} 位客户`,
          onChange: setPage,
        }}
        locale={{ emptyText: roster.isError ? errText(roster.error, '加载失败') : '这位销售名下还没有客户' }}
      />

      {canTransfer && (
        <Form
          name="team_transfer"
          form={form}
          layout="vertical"
          preserve={false}
          onFinish={(v) => transfer.mutate({ customerIds: selected, toAdminId: v.toAdminId, reason: v.reason.trim() })}
        >
          <Form.Item name="toAdminId" label="接收的销售" rules={[{ required: true, message: '请选择接收人' }]}>
            <Select
              placeholder="选择接收客户的销售"
              loading={salesList.isLoading}
              // 不含被转出的这位销售；没有 advisor:manage 的人也不能把客户转给自己（服务端同样会拒）
              options={(salesList.data ?? [])
                .filter((s) => s.id !== sales.id && (canManage || s.id !== actorId))
                .map((s) => ({ value: s.id, label: s.nickname }))}
            />
          </Form.Item>
          <Form.Item
            name="reason"
            label="转交理由"
            validateTrigger={['onChange', 'onBlur']}
            rules={[
              {
                validator: (_, value?: string) => {
                  const e = reasonError(value);
                  return e ? Promise.reject(new Error(e)) : Promise.resolve();
                },
              },
            ]}
            extra={`${reasonLength(reason)} / ${TRANSFER_REASON_MAX} 字（至少 ${TRANSFER_REASON_MIN} 字）`}
          >
            <Input.TextArea rows={3} placeholder="如：客户搬到外地，换同城的销售跟进" />
          </Form.Item>
          <Button type="primary" disabled={selected.length === 0} loading={transfer.isPending} onClick={() => form.submit()}>
            确认转交{selected.length ? `（${selected.length} 位）` : ''}
          </Button>
        </Form>
      )}
    </Space>
  );
}

export interface TeamRosterDrawerProps {
  /** 要看哪位销售的名册；null = 关闭 */
  sales: SalesOverviewRowDto | null;
  onClose: () => void;
}

export default function TeamRosterDrawer({ sales, onClose }: TeamRosterDrawerProps) {
  return (
    <Drawer
      title={sales ? `「${sales.nickname}」的客户名册` : '客户名册'}
      open={!!sales}
      onClose={onClose}
      width={860}
      destroyOnHidden
    >
      {/* key：换一位销售就整体重挂，勾选与表单状态不串 */}
      {sales && <RosterBody key={sales.id} sales={sales} />}
    </Drawer>
  );
}
